import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { produce } from 'immer';
import { useCallback } from 'react';

import { getHttpClient, HttpClient, useHttpClient } from '@bonusly/http-client';
import { getQueryClient } from '@bonusly/query-client';
import { Permission, usePermission } from '@bonusly/who-am-i';

import { AdminNominationPeriodDetail, AdminNominationPeriodIndex, Nomination, UserNominationPeriod } from '../schemas/nominations';
import { listOf } from '../schemas/utils';
import { toTimelessDateString } from '../utils/date';
import { UserGroups } from './groups';
import { ensureID, Metadata, PaginatedData, RecordOrID } from './meta';

/*
 * Shared utilities
 */
enum QueryKey {
	Admin = 'nominations:admin-nomination-periods',
	User = 'nominations:user-nomination-periods',
	Meta = 'nominations:metadata',
}

export const ruleConditionAttrToRuleOptionsGroup: Record<any, keyof UserGroups> = {
	manager_id: 'teams',
	location: 'locations',
	department: 'departments',
	'has_reports?': 'roles',
};

// This is what comes back from the API as the acutally applied rules, i.e. part of the model
export type RuleCondition = {
	attr: string;
	value: string;
};

/*
 * Nomination Periods API
 */

// TODO: Create utilities to make these query keys more type-safe
export const adminNominationPeriodQueryKey = (id: string) => [QueryKey.Admin, id];
export const adminNominationPeriodsQueryKey = (params?: Record<string, any>) => [QueryKey.Admin, params].filter(Boolean);
export const userNominationPeriodsQueryKey = (params?: Record<string, any>) => [QueryKey.User, params].filter(Boolean);

export const adminNominationPeriodsPath = (prefix = '') => `${prefix}/admin/nomination_periods`;
export const adminNominationPeriodPath = (id: string, prefix = '') => `${prefix}/admin/nomination_periods/${id}`;
export const adminNominationPeriodFavoritesPath = (id: string, prefix = '') => `${prefix}/admin/nomination_periods/${id}/favorites`;
export const adminNominationPeriodCloseNowPath = (id: string, prefix = '') => `${prefix}/admin/nomination_periods/${id}/close_now`;
export const userNominationPeriodsPath = (prefix = '') => `${prefix}/nomination_periods`;

type AdminNominationPeriodAPIData = {
	id: string;
	companyId: string;
	description: string;
	endDate: string;
	nominationLimit: number;
	startDate: string;
};

export const useAdminNominationPeriods = (params?: Record<string, any>) => {
	const http = useHttpClient();

	return useQuery({
		queryKey: adminNominationPeriodsQueryKey(params),
		queryFn: async () => {
			const res = await http.get<PaginatedData<AdminNominationPeriodAPIData>>(adminNominationPeriodsPath(), {
				params,
				headers: {
					'Key-Inflection': 'camel',
				},
			});

			return [listOf(AdminNominationPeriodIndex).parse(res.data.records), new Metadata(res.data.metadata)] as const;
		},
	});
};

const createAdminNominationPeriodFetcher = (http: HttpClient, id: string) => async () => {
	const res = await http.get<AdminNominationPeriodAPIData>(adminNominationPeriodPath(id), {
		headers: {
			'Key-Inflection': 'camel',
		},
	});

	return AdminNominationPeriodDetail.parse(res.data);
};

export const useAdminNominationPeriod = (id: string) => {
	return useQuery({
		queryKey: adminNominationPeriodQueryKey(id),
		queryFn: createAdminNominationPeriodFetcher(useHttpClient(), id),
	});
};

export const loadAdminNominationPeriod = async (id: string) => {
	const http = await getHttpClient();
	const queryClient = await getQueryClient();

	return queryClient.fetchQuery({
		queryKey: adminNominationPeriodQueryKey(id),
		queryFn: createAdminNominationPeriodFetcher(http, id),
	});
};

type UserNominationPeriodAPIData = {
	id: string;
	companyId: string;
	description: string;
	endDate: string;
	nominationLimit: number;
	startDate: string;
};

// NOTE: This uses a paginated query interface, but at this time we're not *actually* paginating the data
// since we don't expect there will ever be a large number of nomination periods. We might have to update
// this hook in the future to handle pagination.

const createUserNominationPeriodsFetcher = (http: HttpClient, params?: Record<string, any>) => async () => {
	const res = await http.get<PaginatedData<UserNominationPeriodAPIData>>(userNominationPeriodsPath(), {
		params,
		headers: {
			'Key-Inflection': 'camel',
		},
	});

	return UserNominationPeriod.array().parse(res.data.records);
};

export const useUserNominationPeriods = (params?: Record<string, any>) => {
	const canAccessNominations = usePermission(Permission.CanAccessNominations);

	const result = useQuery({
		queryKey: userNominationPeriodsQueryKey(params),
		queryFn: createUserNominationPeriodsFetcher(useHttpClient(), params),
		enabled: canAccessNominations,
	});

	// Return empty data and no loading state when user lacks permissions
	if (!canAccessNominations) {
		return {
			data: [],
			isLoading: false,
			isError: false,
			error: null,
		};
	}

	return result;
};

export const loadUserNominationPeriods = async () => {
	const http = await getHttpClient();
	const queryClient = await getQueryClient();

	return queryClient.fetchQuery({
		queryKey: userNominationPeriodsQueryKey(),
		queryFn: createUserNominationPeriodsFetcher(http),
	});
};

export type MutateNominationPeriodOptions = {
	onError?: (error: unknown) => void;
	onSuccess?: (record: AdminNominationPeriodDetail) => void;
};

export type MutateNominationPeriodAPIData = {
	name: string;
	description: string;
	startDate: string | null;
	endDate: string | null;
	nominationLimit: number | null;
	nomineeRuleConditions: RuleCondition[];
	nominatorRuleConditions: RuleCondition[];
};

// Create a new record
export const useCreateNominationPeriod = (options: MutateNominationPeriodOptions = {}) => {
	const http = useHttpClient();
	const store = useStoreAdminNominationPeriod();

	return useMutation({
		mutationFn: async (data: MutateNominationPeriodAPIData) => {
			try {
				const res = await http.post(adminNominationPeriodsPath(), data, {
					headers: {
						'Key-Inflection': 'camel',
					},
				});
				const record = AdminNominationPeriodDetail.parse(res.data);
				store(record);
				options.onSuccess?.(record);
			} catch (e) {
				options.onError?.(e);
				throw e;
			}
		},
	});
};

// Update multiple fields on a record
export const useUpdateNominationPeriod = (recordOrId: RecordOrID, options: MutateNominationPeriodOptions = {}) => {
	const http = useHttpClient();
	const store = useStoreAdminNominationPeriod();
	const invalidate = useInvalidateNominationPeriods();

	return useMutation({
		mutationFn: async (data: MutateNominationPeriodAPIData) => {
			try {
				const res = await http.patch(adminNominationPeriodPath(ensureID(recordOrId)), data, {
					headers: {
						'Key-Inflection': 'camel',
					},
				});
				const record = AdminNominationPeriodDetail.parse(res.data);
				store(record);
				invalidate();
				options.onSuccess?.(record);
			} catch (e) {
				options.onError?.(e);
				throw e;
			}
		},
	});
};

// Bespoke mutations to abstract the functionality from the dev
export const useAddFavoriteNomination = (recordOrId: RecordOrID, options: MutateNominationPeriodOptions = {}) => {
	const http = useHttpClient();
	const store = useStoreAdminNominationPeriod();
	const fetch = useFetchAdminNominationPeriod();

	return useMutation({
		mutationFn: async (userId: string) => {
			const existing = fetch(ensureID(recordOrId));

			if (existing) {
				const updated = produce(existing, (draft) => {
					draft.favoritedUserIds.push(userId);
				});
				store(updated);
			}

			try {
				const res = await http.patch(
					adminNominationPeriodFavoritesPath(ensureID(recordOrId)),
					{ userIdsToAdd: [userId] },
					{
						headers: {
							'Key-Inflection': 'camel',
						},
					}
				);
				const record = AdminNominationPeriodDetail.parse(res.data);
				store(record);
				options.onSuccess?.(record);
			} catch (e) {
				if (existing) {
					store(existing);
				}
				options.onError?.(e);
				throw e;
			}
		},
	});
};

export const useRemoveFavoriteNomination = (recordOrId: RecordOrID, options: MutateNominationPeriodOptions = {}) => {
	const http = useHttpClient();
	const store = useStoreAdminNominationPeriod();
	const fetch = useFetchAdminNominationPeriod();

	return useMutation({
		mutationFn: async (userId: string) => {
			const existing = fetch(ensureID(recordOrId));

			if (existing) {
				const updated = produce(existing, (draft) => {
					draft.favoritedUserIds = draft.favoritedUserIds.filter((id) => id !== userId);
				});
				store(updated);
			}

			try {
				const res = await http.patch(
					adminNominationPeriodFavoritesPath(ensureID(recordOrId)),
					{ userIdsToRemove: [userId] },
					{
						headers: {
							'Key-Inflection': 'camel',
						},
					}
				);
				const record = AdminNominationPeriodDetail.parse(res.data);
				store(record);
				options.onSuccess?.(record);
			} catch (e) {
				if (existing) {
					store(existing);
				}
				options.onError?.(e);
				throw e;
			}
		},
	});
};

export const useShareNominationPeriod = (recordOrId: RecordOrID, options: MutateNominationPeriodOptions = {}) => {
	const http = useHttpClient();
	const store = useStoreAdminNominationPeriod();
	const fetch = useFetchAdminNominationPeriod();

	return useMutation({
		mutationFn: async () => {
			const existing = fetch(ensureID(recordOrId));

			if (existing) {
				const updated = produce(existing, (draft) => {
					draft.visibility = 'public';
				});
				store(updated);
			}

			try {
				const res = await http.patch(
					adminNominationPeriodPath(ensureID(recordOrId)),
					{ visibility: 'public' },
					{
						headers: {
							'Key-Inflection': 'camel',
						},
					}
				);

				const record = AdminNominationPeriodDetail.parse(res.data);
				store(record);
				options.onSuccess?.(record);
			} catch (e) {
				if (existing) {
					store(existing);
				}
				options.onError?.(e);
				throw e;
			}
		},
	});
};

export const useRestrictNominationPeriod = (recordOrId: RecordOrID, options: MutateNominationPeriodOptions = {}) => {
	const http = useHttpClient();
	const store = useStoreAdminNominationPeriod();
	const fetch = useFetchAdminNominationPeriod();

	return useMutation({
		mutationFn: async () => {
			const existing = fetch(ensureID(recordOrId));

			if (existing) {
				const updated = produce(existing, (draft) => {
					draft.visibility = 'admin';
				});
				store(updated);
			}

			try {
				const res = await http.patch(
					adminNominationPeriodPath(ensureID(recordOrId)),
					{ visibility: 'admin' },
					{
						headers: {
							'Key-Inflection': 'camel',
						},
					}
				);
				const record = AdminNominationPeriodDetail.parse(res.data);
				store(record);
				options.onSuccess?.(record);
			} catch (e) {
				if (existing) {
					store(existing);
				}
				options.onError?.(e);
				throw e;
			}
		},
	});
};

export const useOpenNominationPeriod = (recordOrId: RecordOrID, options: MutateNominationPeriodOptions = {}) => {
	const http = useHttpClient();
	const store = useStoreAdminNominationPeriod();
	const invalidate = useInvalidateNominationPeriods();

	return useMutation({
		mutationFn: async () => {
			try {
				const res = await http.patch(
					adminNominationPeriodPath(typeof recordOrId === 'string' ? recordOrId : recordOrId.id),
					{ startDate: toTimelessDateString(new Date()) },
					{
						headers: {
							'Key-Inflection': 'camel',
						},
					}
				);
				const record = AdminNominationPeriodDetail.parse(res.data);
				store(record);
				invalidate();
				options.onSuccess?.(record);
			} catch (e) {
				options.onError?.(e);
				throw e;
			}
		},
	});
};

export const useCloseNominationPeriod = (options: MutateNominationPeriodOptions = {}) => {
	const http = useHttpClient();
	const store = useStoreAdminNominationPeriod();
	const invalidate = useInvalidateNominationPeriods();

	return useMutation({
		mutationFn: async (recordOrId: RecordOrID, mutateOptions: MutateNominationPeriodOptions = {}) => {
			const allOptions = { ...options, ...mutateOptions };

			try {
				const id = ensureID(recordOrId);
				const res = await http.patch(
					adminNominationPeriodCloseNowPath(id),
					{},
					{
						headers: {
							'Key-Inflection': 'camel',
						},
					}
				);
				const record = AdminNominationPeriodDetail.parse(res.data);
				store(record);
				invalidate();
				allOptions.onSuccess?.(record);
			} catch (e) {
				allOptions.onError?.(e);
				throw e;
			}
		},
	});
};

// Manual cache management
export const useInvalidateNominationPeriods = () => {
	const client = useQueryClient();

	return useCallback(() => {
		client.invalidateQueries(adminNominationPeriodsQueryKey());
		client.invalidateQueries(userNominationPeriodsQueryKey());
	}, [client]);
};

export const useStoreAdminNominationPeriod = () => {
	const queryClient = useQueryClient();

	return useCallback(
		(record: AdminNominationPeriodDetail) => {
			queryClient.setQueryData<AdminNominationPeriodDetail>(adminNominationPeriodQueryKey(record.id), record);
		},
		[queryClient]
	);
};

export const useFetchAdminNominationPeriod = () => {
	const queryClient = useQueryClient();

	return useCallback(
		(id: string) => {
			return queryClient.getQueryData<AdminNominationPeriodDetail>(adminNominationPeriodQueryKey(id));
		},
		[queryClient]
	);
};

type DeleteNominationPeriodOptions = {
	onError?: (error: unknown) => void;
	onSuccess?: () => void;
};

export const useDeleteNominationPeriod = (options: DeleteNominationPeriodOptions = {}) => {
	const http = useHttpClient();
	const invalidate = useInvalidateNominationPeriods();

	return useMutation({
		mutationFn: async (recordOrId: RecordOrID, mutateOptions: DeleteNominationPeriodOptions = {}) => {
			const allOptions = { ...options, ...mutateOptions };

			try {
				const id = typeof recordOrId === 'string' ? recordOrId : recordOrId.id;
				await http.delete(adminNominationPeriodPath(id));
				invalidate();
				allOptions.onSuccess?.();
			} catch (e) {
				allOptions.onError?.(e);
				throw e;
			}
		},
	});
};
/*
 * Nominations API
 */

export const userNominationsPath = (prefix = '') => `${prefix}/nominations`;

type NominationAPIData = {
	id: string;
	reason: string;
	nominationPeriodId: string;
	nominatedUserId: string;
	nominatorId: string;
};

type MutateNominationOptions = {
	onError?: (error: unknown) => void;
	onSuccess?: (nomination: Nomination) => void;
};

type CreateNominationAPIData = {
	reason: string;
	nominationPeriodId: string;
	nominatedUserId: string;
};

export const useCreateNomination = (options: MutateNominationOptions = {}) => {
	const http = useHttpClient();
	const invalidate = useInvalidateNominationPeriods();

	return useMutation({
		mutationFn: async (data: CreateNominationAPIData, mutateOptions: MutateNominationOptions = {}) => {
			const allOptions = { ...options, ...mutateOptions };

			try {
				const res = await http.post<NominationAPIData>(userNominationsPath(), data, {
					headers: {
						'Key-Inflection': 'camel',
					},
				});
				const nomination = Nomination.parse(res.data);

				invalidate();
				allOptions.onSuccess?.(nomination);

				return nomination;
			} catch (e) {
				options.onError?.(e);
				throw e;
			}
		},
	});
};

/*
 * Combinational queries
 */

export const useNominationsCount = () => {
	const { data = [], isLoading } = useUserNominationPeriods();

	// Right now this is only filtering by status, but once the user's nomination is included in the data
	// we'll also need to filter on that
	return {
		incomplete: data.filter((period) => period.status === 'open' && !period.currentUserNominations.length).length,
		open: data.filter((period) => period.status === 'open').length,
		isLoading,
	};
};
